import { setDataValue } from '../../form-sync';
import moment from '../../../modules/moment';

export default {
    setDataValue,

    /**
     * Set the reservation data
     *
     * @param state
     * @param data
     */
    setReservation(state, data) {
        Object.assign(state.reservation_details.bookings[0].reservation, data);
    },

    /**
     * Set which steps are valid. Invalidating a previous
     * step will also invalidate any steps in after it.
     *
     * This forces the user to ensure the data they
     * are entering is as expected at the payment stage.
     *
     * If they ever want to be able to flick backwards
     * and forwards we need to implement read-only
     * confirmed data so that when the user updates
     * the dates/times without confirming it isn't
     * automatically reflected in the reservations
     * summary due to reactivity
     *
     * @param state
     * @param activeStep
     */
    setValidStep(state, activeStep) {
        const steps = Object.keys(state.validSteps);

        // Loop through the steps and decide whether
        // it should remain valid
        steps.forEach((step) => {
            state.validSteps[step] = steps.indexOf(step) <= steps.indexOf(activeStep);
        });

        state.activeStep = steps[steps.indexOf(activeStep) + 1];
    },

    /**
     * Show or hide a specific step
     *
     * @param state
     * @param step
     */
    toggleStep(state, step) {
        state.activeStep = step;
    },

    /**
     * Set the data to an existing booking
     *
     * @param {Object} state
     * @param {Object} booking
     */
    setExistingBooking(state, booking) {
        const startMoment = new moment(booking.start);
        const existingBooking = {
            id: booking.id,
            start_date: startMoment.format('DD-MM-YYYY'),
            start_time: startMoment.format('HH:mm'),
            end_date: null,
            end_time: null,
            reservation: {
                price: booking.price,
                monthly_price: booking.monthly_price,
            },
            permit_address: booking.permit_address,
        };

        if (booking.end) {
            const endMoment = new moment(booking.end);
            existingBooking.end_date = endMoment.format('DD-MM-YYYY');
            existingBooking.end_time = endMoment.format('HH:mm');
        }

        Object.assign(state.reservation_details.bookings[0], existingBooking);

        Object.keys(state.reservation_details.bookings[0])
            .filter((key) => booking[key])
            .forEach((key) => {
                state.reservation_details.bookings[0][key] = existingBooking[key];
            });

        if (state.voucher) {
            state.voucher.id = booking.voucher.id;
            state.voucher.name = booking.voucher.name;
            state.voucher.label = booking.voucher.label;
        }

        if (state.space) {
            state.space.id = booking.space.id;
            state.space.title = booking.space.title;
        }
    },

    /**
     * Set the vehicle on the booking
     *
     * @param {Object} state
     * @param {Object} vehicle
     */
    setVehicle(state, vehicle) {
        state.account_information.vehicle.id = vehicle.id;
        state.account_information.vehicle.registration_number = vehicle.registration_number;
        state.account_information.vehicle.reference = vehicle.reference
            || vehicle.registration_number;
    },

    /**
     * Set the purchased products on the booking
     *
     * @param {Object} state
     * @param {Object} products
     */
    setProducts(state, products) {
        state.products = products;
    },

    /**
     * Set the space data
     *
     * @param {Object} state
     * @param {Object} space
     */
    setSpace(state, space) {
        state.space = space;

        if (space.lt_season_pricing === 'season' && space.lt_season_pricing.data.length) {
            const lowestInterval = space.lt_season_pricing.data.concat().sort((a, b) => {
                return (a.billing.interval_count < b.billing.interval_count)
                    ? -1
                    : 1;
            })[0];

            state.reservation_details.interval_count = lowestInterval.billing.interval_count;
        }
    },

    /**
     * Set the reservation interval count
     *
     * @param {Object} state
     * @param {Number} interval
     */
    setIntervalCount(state, interval) {
        state.reservation_details.interval_count = interval;
    },

    /**
     * Set the reservations bookings data
     *
     * @param {Object} state
     * @param {Object} booking
     */
    updateBookingData(state, booking) {
        Object.assign(state.reservation_details.bookings.find(b => b.key === booking.key), booking);
    },

    /**
     * Set the reservation data
     *
     * @param {Object} state
     * @param {Object} data
     */
    updateReservationData(state, data) {
        Object.assign(state.reservation_details, data);
    },

    /**
     * Set the reservation
     *
     * @param {Object} state
     * @param {Object} data
     */
    updateReservation(state, data) {
        Object.assign(state.reservation_details.reservation, data);
    },

    /**
     * Set the voucher
     *
     * @param {Object} state
     * @param {Object} data
     */
    updateVoucher(state, data) {
        Object.assign(state.reservation_details.voucher, data);
    },

    /**
     * Update all bookings with data.
     *
     * @param {Object} state
     */
    resetReservation(state) {
        state.reservation_details.voucher = {
            id: null,
            name: null,
            label: null,
            discounted_value: null,
        };

        state.reservation_details.bookings.forEach((booking) => {
            booking.reservation.reservation_id = null;
            booking.errors = [];
        });
    },

    /**
     * Add a new booking to the bookings array, auto increment the key and then add a day to the start_date.
     *
     * @param {Object} state
     * @param {Object} booking
     */
    addNewBooking(state, booking) {
        state.reservation_details.bookings.push(booking);
    },

    /**
     * Set the reservations bookings data
     *
     * @param {Object} state
     * @param {String} key
     */
    removeBooking(state, key) {
        state.reservation_details.bookings.splice(
            state.reservation_details.bookings.findIndex(b => b.key === key),
            1
        );
    },

    setDuration(state, duration) {
        state.reservation_details.duration = duration;
    },

    setEndDate(state, end_date) {
        state.reservation_details.bookings[0].end_date = end_date;
    },

    setEndTime(state, end_time) {
        state.reservation_details.bookings[0].end_time = end_time;
    },

    setOverDay(state, value) {
        state.overDay = value;
    },

    setServiceFeesStatus(state, status) {
        state.serviceFeesStatus = status;
        state.reservation_details.bookings[0].serviceFeesStatus = status;
    },

    setServiceFee(state, value) {
        state.serviceFee = value;
    },
};
